import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, BadgeCheck, BookOpen, CalendarDays, ChevronDown, ChevronLeft, ClipboardList, CreditCard, Download, Eye, FileText, GraduationCap, History, IdCard, Loader2, Lock, MapPin, ShieldCheck, X } from "lucide-react";
import { getCourseProgressHistory, useStudentCourseProgress, validateCourseProgressStepAccess } from "@/hooks/useStudentCourseProgress";
import { canDownloadEnrollmentDocument, EnrollmentActionStatusMap, getStatusMeta } from "@/utils/enrolmentStatus";
import { COURSE_STEP_STATUS } from "@/utils/courseStepStatus";
import { COURSE_STEP_TYPE } from "@/utils/courseStepType";
import { getCourseProgressPermissions } from "@/utils/courseProgressPermissions";
import { adminAttendanceService } from "@/services/adminAttendanceService";
import { adminEnrolmentService } from "@/services/adminEnrolmentService";
import { enrollmentDocumentService } from "@/services/enrollmentDocumentService";
import { studentEnrollmentWorkflowService } from "@/services/studentEnrollmentWorkflowService";
import { studentAssessmentService } from "@/services/studentAssessmentService";
import { studentUSIService } from "@/services/studentUSIService";
import CourseProgressSummary from "@/components/course-progress/CourseProgressSummary";
import WorkflowHistoryTimeline from "@/components/WorkflowHistoryTimeline";
import ToastHelper from "@/utils/toastHelper";
import { getStageUrl } from "@/components/course-progress/courseProgressNavigation";
import { CertificateDialog, emptyCertificateForm } from "@/pages/admin/AdminCertificates";
import { certificateService } from "@/services/certificateService";
import CourseStepValidationDialog from "@/components/course-progress/CourseStepValidationDialog";
import { formatDate } from "@/utils/dateUtils";

const iconMap = {
  "badge-check": BadgeCheck,
  "book-open": BookOpen,
  "calendar-days": CalendarDays,
  "clipboard-list": ClipboardList,
  "credit-card": CreditCard,
  "file-text": FileText,
  "graduation-cap": GraduationCap,
  "id-card": IdCard,
};

const formatDateTime = (value) => value
  ? new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "-";

const STEP_STATUS = {
  LOCKED: COURSE_STEP_STATUS.LOCKED,
  AVAILABLE: COURSE_STEP_STATUS.AVAILABLE,
  IN_PROGRESS: COURSE_STEP_STATUS.IN_PROGRESS,
  COMPLETED: COURSE_STEP_STATUS.COMPLETED,
  ACTION_REQUIRED: COURSE_STEP_STATUS.ACTION_REQUIRED,
};
const DISPLAY_STATUS = {
  Available: "Available",
  InProgress: "In Progress",
  ActionRequired: "Action Required",
  PendingReview: "Pending Review",
  Submitted: "Submitted",
  Completed: "Completed",
  Locked: "Locked",
};

const STATUS_STYLE = {
  Completed: "Completed",
  Pending: "Pending",
  Available: "Available",
  Locked: "Locked",
  Error: "Error",
};

const STATUS_COLOUR = {
  Green: "Green",
  Amber: "Amber",
  Orange: "Orange",
  Grey: "Grey",
  Red: "Red",
};

const getStepStatusNo = (step) => Number(step?.stepStatusNo ?? step?.statusNo ?? 0);
const getCourseStepElementId = (key) => `course-step-${String(key || "").replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const getStepStatus = (step) => {
  if (step?.stepStatus) return step.stepStatus;

  const statusNo = getStepStatusNo(step);
  if (statusNo === STEP_STATUS.COMPLETED) return "Completed";
  if (statusNo === STEP_STATUS.ACTION_REQUIRED) return "Action Required";
  if (statusNo === STEP_STATUS.IN_PROGRESS) return "In Progress";
  if (statusNo === STEP_STATUS.AVAILABLE) return "Available";
  return "Locked";
};

const getWorkflowStatusName = (item = {}) => item.workflowStatusName
  || item.workFlowStatusName
  || item.WorkFlowStatusName
  || item.workflowStatus
  || item.workFlowStatus
  || item.WorkFlowStatus
  || "";

const LLN_STEP_TYPE = COURSE_STEP_TYPE.LLN_TEST;
const BOOK_CLASS_STEP_TYPE = COURSE_STEP_TYPE.BOOK_CLASS;
const ATTENDANCE_STEP_TYPE = COURSE_STEP_TYPE.ATTENDANCE;
const ASSESSMENT_STEP_TYPE = COURSE_STEP_TYPE.ASSESSMENT;
const COURSE_COMPLETED_STEP_TYPE = COURSE_STEP_TYPE.COURSE_COMPLETED;
const CERTIFICATE_STEP_TYPE = COURSE_STEP_TYPE.CERTIFICATE;
const ATTENDANCE_STATUS = {
  Present: 1,
};
const ATTENDANCE_STATUS_OPTIONS = [
  { id: 1, label: "Present" },
  { id: 2, label: "Absent" },
  { id: 3, label: "Late" },
  { id: 4, label: "Excused" },
  { id: 5, label: "Left Early" },
];

const getCourseStepTypeNo = (item = {}) => Number(item?.stepTypeNo ?? item?.courseStepTypeNo ?? item?.stepType ?? 0);
const getStepHistoryKey = (item = {}) => {
  const stepTypeNo = getCourseStepTypeNo(item);
  return stepTypeNo > 0 ? `step-${stepTypeNo}` : "";
};

const isPreClassItem = (item = {}) => getCourseStepTypeNo(item) === LLN_STEP_TYPE;

const isCompetencyItem = (item = {}) => [COURSE_STEP_TYPE.PRE_TRAINING_REVIEW, COURSE_STEP_TYPE.LLN_TEST, COURSE_STEP_TYPE.PRE_CLASS_PREPARATION].includes(getCourseStepTypeNo(item));

const isReadingResourcesItem = (item = {}) => [COURSE_STEP_TYPE.READING_LEARNING_PACK, COURSE_STEP_TYPE.READING_VICTORIAN_SAFETY, COURSE_STEP_TYPE.READING_WORKSAFE].includes(getCourseStepTypeNo(item));

const isBookClassItem = (item = {}) => getCourseStepTypeNo(item) === BOOK_CLASS_STEP_TYPE;
const isCourseCompletedItem = (item = {}) => getCourseStepTypeNo(item) === COURSE_COMPLETED_STEP_TYPE;
const isCertificateItem = (item = {}) => getCourseStepTypeNo(item) === CERTIFICATE_STEP_TYPE;

const isEnrolmentItem = (item = {}) => String(item.key || item.stepCode || "").toLowerCase().includes("enrol")
  || String(item.title || item.stepName || "").toLowerCase().includes("enrolment");

const normalizeWorkflowAction = (button = {}) => button.text ?? button.Text ?? button.action ?? button.Action ?? button.name ?? button.Name ?? "";

const getWorkflowButtonNextStatus = (button = {}) => {
  const nextStatus = Number(button?.nextWorkFlowStatusNo ?? button?.NextWorkFlowStatusNo);
  if (Number.isFinite(nextStatus) && nextStatus > 0) return nextStatus;
  const action = normalizeWorkflowAction(button);
  return EnrollmentActionStatusMap[action] || 0;
};

const getWorkflowButtonNextStatusFromEvent = (event, button = {}) => {
  const datasetStatus = Number(event?.currentTarget?.dataset?.workflowStatusNo);
  if (Number.isFinite(datasetStatus) && datasetStatus > 0) return datasetStatus;
  const legacyDatasetStatus = Number(event?.currentTarget?.dataset?.nextWorkflowStatusNo);
  if (Number.isFinite(legacyDatasetStatus) && legacyDatasetStatus > 0) return legacyDatasetStatus;
  const fallbackStatus = getWorkflowButtonNextStatus(button);
  return Number.isFinite(fallbackStatus) && fallbackStatus > 0 ? fallbackStatus : 0;
};

const getAssessmentSummary = (assessment) => {
  if (!assessment?.attemptId) return null;
  const total = assessment.result?.totalQuestions || assessment.totalQuestions || assessment.sections?.reduce((count, section) => count + (section.questions?.length || 0), 0) || 0;
  const answered = assessment.result?.answeredQuestions || assessment.answers?.length || 0;
  const correct = assessment.result?.correctQuestions ?? assessment.answers?.filter((answer) => answer.isCorrect).length ?? 0;
  const wrong = Math.max(answered - correct, 0);
  const support = total > 0 && correct < Math.ceil(total * 0.67);
  const status = Number(assessment.attemptStatusNo) === 3 ? "Completed" : "In progress";
  const nextQuestionNo = Number(assessment.nextQuestionNo || 0);
  const resumeText = status === "In progress" && nextQuestionNo > 0 ? `Resume from Question ${nextQuestionNo}` : "";
  const trainerReviewRequired = Boolean(assessment.result?.trainerReviewRequired ?? assessment.trainerReviewRequired);
  return { total, answered, correct, wrong, support, status, resumeText, trainerReviewRequired };
};

const normalizeCourseSteps = (steps = []) => [{
  key: "course-steps",
  title: "Course progress",
  subheading: "Complete each required step in the order provided.",
  items: steps
    .filter(Boolean)
    .sort((left, right) => Number(left.sequenceNo || 0) - Number(right.sequenceNo || 0))
    .map((step) => {
      const statusNo = getStepStatusNo(step);
      const status = getStepStatus(step);
      return {
        ...step,
        key: step.courseStepId || step.courseStepMasterId || step.stepCode,
        icon: step.icon || "file-text",
        title: step.stepName || step.stepCode || "Course step",
        description: step.stepDescription || "",
        status,
        statusNo,
        isCompleted: statusNo === STEP_STATUS.COMPLETED,
        isLocked: statusNo === STEP_STATUS.LOCKED,
        isStudentAction: step.isStudentAction ?? step.IsStudentAction ?? false,
        requiresStudentAction: step.requiresStudentAction ?? step.RequiresStudentAction ?? false,
        canOpen: step.canOpen === true,
        url: step.actionUrl || step.url || "",
        studentWorkflowStatusNo: Number(step.studentWorkflowStatusNo ?? step.StudentWorkflowStatusNo ?? step.displayStatusNo ?? step.DisplayStatusNo ?? 0),
        studentWorkflowStatus: step.studentWorkflowStatus || step.StudentWorkflowStatus || step.displayStatus || step.DisplayStatus || "",
        displayStatus: step.displayStatus || step.DisplayStatus || "",
        statusStyleNo: Number(step.statusStyleNo ?? step.StatusStyleNo ?? 0),
        statusStyle: step.statusStyle || step.StatusStyle || "",
        statusColour: step.statusColour || step.StatusColour || "",
        workflowStatusName: getWorkflowStatusName(step),
        reviewStatus: step.reviewStatus || step.ReviewStatus || "",
        reviewStatusText: step.reviewStatusText || step.ReviewStatusText || "",
        studentStatusMessage: step.studentStatusMessage || step.StudentStatusMessage || step.statusMessage || step.StatusMessage || "",
        actionType: step.actionType || step.ActionType || "",
        primaryActionText: step.primaryActionText || step.PrimaryActionText || "",
        statusMessage: step.statusMessage || step.StatusMessage || "",
        actionText: step.primaryActionText || step.PrimaryActionText || (status === "ActionRequired" ? "Continue" : status === "Completed" ? "View" : status === "InProgress" ? "Continue" : "Start"),
        completionDate: step.completedDt,
      };
    }),
}];

const normalizeProgressItem = (item = {}) => ({
  ...item,
  studentWorkflowStatusNo: Number(item.studentWorkflowStatusNo ?? item.StudentWorkflowStatusNo ?? item.displayStatusNo ?? item.DisplayStatusNo ?? 0),
  studentWorkflowStatus: item.studentWorkflowStatus || item.StudentWorkflowStatus || item.displayStatus || item.DisplayStatus || "",
  displayStatus: item.displayStatus || item.DisplayStatus || "",
  statusStyleNo: Number(item.statusStyleNo ?? item.StatusStyleNo ?? 0),
  statusStyle: item.statusStyle || item.StatusStyle || "",
  statusColour: item.statusColour || item.StatusColour || "",
  workflowStatusName: getWorkflowStatusName(item),
  reviewStatus: item.reviewStatus || item.ReviewStatus || "",
  reviewStatusText: item.reviewStatusText || item.ReviewStatusText || "",
  studentStatusMessage: item.studentStatusMessage || item.StudentStatusMessage || item.statusMessage || item.StatusMessage || "",
  requiresStudentAction: item.requiresStudentAction ?? item.RequiresStudentAction ?? false,
  isStudentAction: item.isStudentAction ?? item.IsStudentAction ?? false,
  actionType: item.actionType || item.ActionType || "",
  primaryActionText: item.primaryActionText || item.PrimaryActionText || "",
  statusMessage: item.statusMessage || item.StatusMessage || "",
});

const normalizeSections = (progress) => {
  if (Array.isArray(progress?.sections) && progress.sections.length > 0) {
    return progress.sections.map((section) => ({
      ...section,
      items: Array.isArray(section.items || section.Items)
        ? (section.items || section.Items).filter(Boolean).map(normalizeProgressItem)
        : [],
    }));
  }
  const courseSteps = progress?.courseSteps || progress?.steps;
  if (Array.isArray(courseSteps)) return normalizeCourseSteps(courseSteps);
  return [];
};

const COURSE_PROGRESS_GROUPS = [
  {
    key: "pre-class-requirements",
    title: "Pre-Class Requirements",
    description: "Complete these items before pre-class learning becomes available.",
  },
  {
    key: "pre-class-learning",
    title: "Pre-Class Learning",
    description: "Prepare for class using the existing learning and reading steps below.",
  },
  {
    key: "in-class-completion",
    title: "In-Class & Completion",
    description: "Follow the existing booking, attendance, assessment, and completion steps in order.",
  },
];

const includesNormalizedText = (value = "", search = "") => String(value)
  .toLowerCase()
  .includes(String(search).toLowerCase());

const isUsiItem = (item = {}) => includesNormalizedText(item.key || item.stepCode || item.title || item.stepName, "usi")
  || includesNormalizedText(item.url, "usi-number");

const isDocumentsItem = (item = {}) => includesNormalizedText(item.key || item.stepCode || item.title || item.stepName, "document")
  || includesNormalizedText(item.key || item.stepCode || item.title || item.stepName, "identity")
  || includesNormalizedText(item.url, "document");

const isAttendanceItem = (item = {}) => getCourseStepTypeNo(item) === ATTENDANCE_STEP_TYPE
  || includesNormalizedText(item?.key || item?.stepCode || item?.title || item?.stepName, "attendance")
  || includesNormalizedText(item?.url, "attendance");

const isReadingResourcesSection = (segment = {}) => includesNormalizedText(segment.sectionKey || segment.key || "", "resource")
  || includesNormalizedText(segment.title, "resource");

const isLearningCompetencySegment = (segment = {}) => segment.title === "Learning Competency Assessment";
const isReadingResourcesSegment = (segment = {}) => segment.title === "Reading Resources";
const getSegmentCategory = (segment = {}, groupKey = "") => {
  if ((segment.items || []).some(isCompetencyItem)) return "learning";
  if ((segment.items || []).some(isReadingResourcesItem)) return "reading";
  if (groupKey === "pre-class-requirements") return "requirements";
  if (groupKey === "in-class-completion") return "in-class";
  return "";
};

const createSegment = ({ key, sectionKey = "", title = "", subheading = "", items = [] }) => ({
  key,
  sectionKey,
  title,
  subheading,
  items: items.map((item) => ({ ...item, sectionKey })),
});

const getProgressGroupKey = (segment = {}) => {
  const items = segment.items || [];

  if (isReadingResourcesSection(segment) || items.some((item) => isCompetencyItem(item) || isReadingResourcesItem(item))) {
    return "pre-class-learning";
  }

  if (items.some((item) => isEnrolmentItem(item) || isDocumentsItem(item) || isUsiItem(item))) {
    return "pre-class-requirements";
  }

  return "in-class-completion";
};

const buildGroupedSections = (sections = []) => {
  const groupedMap = new Map(COURSE_PROGRESS_GROUPS.map((group) => [group.key, { ...group, segments: [] }]));

  sections.forEach((section) => {
    const items = Array.isArray(section.items) ? section.items : [];
    if (items.length === 0) return;

    if (section.key === "course-steps") {
      items.forEach((item) => {
        const competencyPart = getCourseStepTypeNo(item) - COURSE_STEP_TYPE.PRE_TRAINING_REVIEW + 1;
        const displayItem = isCompetencyItem(item) ? { ...item, title: `Part ${competencyPart}: ${item.title}` } : item;
        const firstCompetency = getCourseStepTypeNo(item) === COURSE_STEP_TYPE.PRE_TRAINING_REVIEW;
        const firstResource = getCourseStepTypeNo(item) === COURSE_STEP_TYPE.READING_LEARNING_PACK;
        const segment = createSegment({
          key: `${section.key}-${item.key}`,
          sectionKey: section.key,
          title: firstCompetency ? "Learning Competency Assessment" : firstResource ? "Reading Resources" : "",
          subheading: firstCompetency ? "Complete the required pre-class competency activities below to confirm your suitability, LLN readiness, and preparation before attending class." : firstResource ? "Review the required course learning materials below before attending class to help prepare for your White Card training and assessment." : "",
          items: [displayItem],
        });
        groupedMap.get(getProgressGroupKey(segment))?.segments.push(segment);
      });
      return;
    }

    const segment = createSegment({
      key: section.key,
      sectionKey: section.key,
      title: section.title || "",
      subheading: section.subheading || "",
      items,
    });

    groupedMap.get(getProgressGroupKey(segment))?.segments.push(segment);
  });

  return COURSE_PROGRESS_GROUPS
    .map((group) => groupedMap.get(group.key))
    .filter((group) => group && group.segments.length > 0);
};

const getStudentProgressMessage = (progress) => {
  const total = Number(progress?.studentTotalStepCount ?? 0);
  const completed = Number(progress?.studentCompletedStepCount ?? 0);
  const remaining = Math.max(total - completed, 0);
  if (Number(progress?.studentProgressPercentage ?? 0) >= 100 && !progress?.studentHasActionRequired) {
    return {
      tone: "green",
      message: "Your required actions are complete.",
      detail: "Gippsland Institute of Technology will review your submissions and manage the remaining course steps. We will notify you if you need to take any further action.",
    };
  }
  if (progress?.studentHasActionRequired) {
    return {
      tone: "blue",
      message: `${remaining || 1} action${remaining === 1 ? "" : "s"} require${remaining === 1 ? "s" : ""} your attention.`,
      detail: "Please review the returned step and complete the requested update.",
    };
  }
  return {
    tone: "blue",
    message: remaining > 0 ? `${remaining} action${remaining === 1 ? "" : "s"} still require your attention.` : "No student action is required right now.",
    detail: "Complete the next available student step to keep your enrolment moving.",
  };
};

const getInstituteProgressMessage = (progress, isAdmin) => {
  const percent = Number(progress?.instituteProgressPercentage ?? 0);
  if (percent >= 100) {
    return {
      tone: "green",
      title: isAdmin ? "Institute Progress" : "Institute Progress",
      message: "Institute review and course administration are complete.",
      detail: "All institute-managed steps have been completed.",
    };
  }
  if (isAdmin) {
    return {
      tone: "blue",
      title: "Institute Progress",
      message: "Institute-managed review and course administration are still in progress.",
      detail: "Use the progress sections below to complete the remaining institute-managed steps.",
    };
  }
  return {
    tone: "blue",
    title: "Institute Progress",
    message: percent === 0 ? "Institute review and course administration have not yet been completed." : "Review and course administration are in progress.",
    detail: "The Institute will manage the remaining course steps after your required preparation is complete.",
  };
};

export default function CourseProgress({ mode = "student" }) {
  const isAdmin = mode === "admin";
  const { studentBookingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { progress, setProgress, loading, error, reload } = useStudentCourseProgress(studentBookingId, { admin: isAdmin });
  const [processingAction, setProcessingAction] = useState(false);
  const [bookClassDialog, setBookClassDialog] = useState(null);
  const [bookClassRemarks, setBookClassRemarks] = useState("");
  const [courseCompleteDialog, setCourseCompleteDialog] = useState(null);
  const [courseCompleteRemarks, setCourseCompleteRemarks] = useState("");
  const [certificateDialog, setCertificateDialog] = useState(false);
  const [assessmentSummary, setAssessmentSummary] = useState(null);
  const [stepHistoryOpen, setStepHistoryOpen] = useState({});
  const [stepHistoryCache, setStepHistoryCache] = useState({});
  const [courseHistoryCache, setCourseHistoryCache] = useState(null);
  const [stepValidationDialog, setStepValidationDialog] = useState(null);
  const [lockedStepDialog, setLockedStepDialog] = useState(null);
  const [attendanceRecord, setAttendanceRecord] = useState(null);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const focusedBookingId = useRef(null);

  useEffect(() => {
    if (!studentBookingId) return;
    let mounted = true;
    (isAdmin ? studentAssessmentService.getAdmin(studentBookingId) : studentAssessmentService.get(studentBookingId))
      .then((assessment) => { if (mounted) setAssessmentSummary(getAssessmentSummary(assessment)); })
      .catch(() => { if (mounted) setAssessmentSummary(null); });
    return () => { mounted = false; };
  }, [isAdmin, studentBookingId]);

  useEffect(() => {
    if (!isAdmin || !studentBookingId) return;
    let mounted = true;
    adminAttendanceService.getAttendanceByStudentBooking(studentBookingId)
      .then((record) => { if (mounted) setAttendanceRecord(record || null); })
      .catch(() => { if (mounted) setAttendanceRecord(null); });
    return () => { mounted = false; };
  }, [isAdmin, studentBookingId]);

  useEffect(() => {
    if (!progress || focusedBookingId.current === String(studentBookingId)) return;

    const normalizedSections = normalizeSections(progress);
    const allItems = normalizedSections.flatMap((section) => section.items || []);
    const legacyFocusStepTypeNo = Number(location.state?.focusCourseStepTypeNo || 0);
    const currentAction = progress.nextAction || getFirstPendingStep(normalizedSections, isAdmin);
    const focusedItem = legacyFocusStepTypeNo > 0
      ? allItems.find((item) => getCourseStepTypeNo(item) === legacyFocusStepTypeNo)
      : allItems.find((item) => String(item.key) === String(currentAction?.key) || item.title === currentAction?.title);
    if (!focusedItem) return;

    focusedBookingId.current = String(studentBookingId);

    const timer = window.setTimeout(() => {
      const target = document.getElementById(getCourseStepElementId(focusedItem.key));
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    }, 0);

    if (location.state?.focusCurrentStep || location.state?.focusCourseStepTypeNo) {
      const remainingState = { ...location.state };
      delete remainingState.focusCurrentStep;
      delete remainingState.focusCourseStepTypeNo;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: remainingState });
    }

    return () => window.clearTimeout(timer);
  }, [isAdmin, location.pathname, location.search, location.state, navigate, progress, studentBookingId]);

  const goToAction = (url, item = {}) => {
    if (!url) return;
    if (isAdmin && (item.isLocked === true || Number(item.statusNo) === COURSE_STEP_STATUS.LOCKED || item.displayStatus === "Locked")) {
      setLockedStepDialog(item);
      return;
    }
    const [baseUrl, query = ""] = url.split("?");
    if (baseUrl === "/student/enrol-new" || baseUrl === "/student/usi-number" || baseUrl === "/student/pre-training-review" || baseUrl === "/student/enrollment-documents" || baseUrl === "/student/lln-test" || baseUrl === "/student/pre-class-preparation" || baseUrl === "/student/pre-class-instruction" || baseUrl === "/student/reading-resources") {
      const queryStepTypeNo = Number(new URLSearchParams(query).get("courseStepTypeNo") || 0);
      navigate(`${getStageUrl(baseUrl, mode, studentBookingId)}${query ? `?${query}` : ""}`, { state: { studentBookingId: Number(studentBookingId), studentEnrollmentId: progress?.studentEnrollmentId || null, settingPerm: progress?.settingPerm || null, courseProgressUrl: `/${isAdmin ? "admin" : "student"}/caurse-progress/${studentBookingId}`, courseStepTypeNo: getCourseStepTypeNo(item) || queryStepTypeNo || null } });
      return;
    }
    navigate(url);
  };

  const downloadEnrollmentForm = async () => {
    try {
      if (isAdmin) {
        await enrollmentDocumentService.downloadAdminEnrollment(studentBookingId, progress?.bookingNumber);
      } else {
        await enrollmentDocumentService.downloadStudentEnrollment(studentBookingId, progress?.bookingNumber);
      }
    } catch (error) {
      ToastHelper.apiError(error, "Unable to download enrolment form.");
    }
  };

  const handleAdminAction = async (event, button) => {
    const nextStatus = getWorkflowButtonNextStatusFromEvent(event, button);
    const action = normalizeWorkflowAction(button);
    if (!progress?.studentEnrollmentId || !nextStatus || !action) return;
    setProcessingAction(true);
    try {
      await adminEnrolmentService.performEnrolmentAction({
        ...progress,
        studentEnrollmentId: progress.studentEnrollmentId,
        action,
        adminComments: "",
        rejectedReason: "",
        currentWorkFlowStatus: {
          ...(progress.currentWorkFlowStatus || progress.CurrentWorkFlowStatus || {}),
          workFlowStatusNo: Number(progress.workFlowStatusNo ?? progress.WorkFlowStatusNo ?? button.currentWorkFlowStatusNo ?? button.CurrentWorkFlowStatusNo ?? 0) || 0,
          workFlowStatusName: progress.workFlowStatusName ?? progress.WorkFlowStatusName ?? "",
          nextWorkFlowStatusNo: nextStatus,
        },
      });
      ToastHelper.success(`${action} action completed.`);
      window.location.reload();
    } catch (error) {
      ToastHelper.apiError(error, `Unable to ${String(action).toLowerCase()} enrolment`);
    } finally {
      setProcessingAction(false);
    }
  };

  const completeAdminStep = async (item, remarks = "") => {
    const courseStepTypeNo = getCourseStepTypeNo(item);
    if (!studentBookingId || !courseStepTypeNo) return;

    setProcessingAction(true);
    try {
      const response = await adminEnrolmentService.completeCourseStep({
        studentBookingId: Number(studentBookingId),
        courseStepTypeNo,
        remarks,
      });
      const updatedProgress = response?.data || response;
      if (updatedProgress?.isSuccess || updatedProgress?.IsSuccess) {
        setProgress(updatedProgress);
      }
      await reload();
      ToastHelper.success(`${item.title || "Course step"} completed.`);
      setBookClassDialog(null);
      setBookClassRemarks("");
      setCourseCompleteDialog(null);
      setCourseCompleteRemarks("");
    } catch (error) {
      ToastHelper.apiError(error, `Unable to complete ${String(item.title || "course step").toLowerCase()}`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleAdminCompleteStep = async (item) => {
    const courseStepTypeNo = getCourseStepTypeNo(item);
    if (!studentBookingId || !courseStepTypeNo || processingAction) return;

    setProcessingAction(true);
    try {
      const validation = await validateCourseProgressStepAccess(studentBookingId, courseStepTypeNo);
      const canProceed = validation?.canProceed ?? validation?.CanProceed ?? false;

      if (!canProceed) {
        setStepValidationDialog({
          ...(validation || { message: "This step cannot be completed yet." }),
          requestedStepTypeNo: courseStepTypeNo,
        });
        return;
      }
    } catch (error) {
      ToastHelper.apiError(error, `Unable to validate ${String(item.title || "course step").toLowerCase()}`);
      return;
    } finally {
      setProcessingAction(false);
    }

    if (isBookClassItem(item)) {
      setBookClassDialog(item);
      setBookClassRemarks("");
      return;
    }
    if (isCourseCompletedItem(item)) {
      setCourseCompleteDialog(item);
      setCourseCompleteRemarks("");
      return;
    }
    await completeAdminStep(item);
  };

  const handleSkipLlnAndProceed = async () => {
    const requestedStepTypeNo = Number(stepValidationDialog?.requestedStepTypeNo || 0);
    if (!studentBookingId || !requestedStepTypeNo || processingAction || progress?.settingPerm?.canSkipLlnStep !== true) return;

    setProcessingAction(true);
    try {
      await adminEnrolmentService.completeCourseStep({
        studentBookingId: Number(studentBookingId),
        courseStepTypeNo: LLN_STEP_TYPE,
        remarks: "LLN Test skipped by admin during verification.",
      });

      const response = await adminEnrolmentService.completeCourseStep({
        studentBookingId: Number(studentBookingId),
        courseStepTypeNo: requestedStepTypeNo,
        remarks: "Ready for Class completed after admin skipped the LLN review step.",
      });
      const updatedProgress = response?.data || response;
      if (updatedProgress?.isSuccess || updatedProgress?.IsSuccess) setProgress(updatedProgress);

      setStepValidationDialog(null);
      await reload();
      ToastHelper.success("LLN step skipped and Ready for Class completed.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to skip the LLN step");
    } finally {
      setProcessingAction(false);
    }
  };

  const saveAttendanceStatus = async (attendanceStatusId) => {
    if (!isAdmin || !studentBookingId || !attendanceRecord || !attendanceStatusId) return;

    setAttendanceSaving(true);
    try {
      await adminAttendanceService.saveAttendance({
        attendanceId: attendanceRecord.attendanceId || 0,
        courseScheduleId: attendanceRecord.courseScheduleId,
        studentId: attendanceRecord.studentId,
        studentBookingId: attendanceRecord.studentBookingId || Number(studentBookingId),
        enrollmentId: attendanceRecord.enrollmentId || progress?.studentEnrollmentId || null,
        attendanceStatusId,
        checkInTime: attendanceRecord.checkInTime || null,
        checkOutTime: attendanceRecord.checkOutTime || null,
        remarks: attendanceRecord.remarks || "",
      });

      const refreshed = await adminAttendanceService.getAttendanceByStudentBooking(studentBookingId);
      setAttendanceRecord(refreshed || null);
      ToastHelper.success("Attendance saved successfully.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save attendance");
    } finally {
      setAttendanceSaving(false);
    }
  };

  const openCertificateDialog = () => setCertificateDialog(true);

  const downloadIssuedCertificate = async () => {
    try {
      const response = await certificateService.list({ search: String(progress?.studentEnrollmentId || ""), page: 1, pageSize: 100 });
      const certificate = (response?.certificates || []).find((entry) => Number(entry.enrollmentId) === Number(progress?.studentEnrollmentId));
      if (!certificate) {
        ToastHelper.error("Issued certificate document was not found.");
        return;
      }
      await certificateService.download(certificate.certificateId, certificate.fileName || `${certificate.certificateNumber || "certificate"}.pdf`);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to download certificate");
    }
  };

  const loadStepHistory = async (item) => {
    const historyKey = getStepHistoryKey(item);
    if (!historyKey) return;
    if (stepHistoryCache[historyKey]?.rows || stepHistoryCache[historyKey]?.loading) return;

    setStepHistoryCache((current) => ({ ...current, [historyKey]: { rows: [], loading: true, error: "" } }));

    try {
      let rows = [];
      if (isEnrolmentItem(item) && progress?.studentEnrollmentId) {
        rows = await (isAdmin
          ? adminEnrolmentService.getWorkflowHistory(progress.studentEnrollmentId)
          : studentEnrollmentWorkflowService.getWorkflowHistory(progress.studentEnrollmentId));
      } else if (isUsiItem(item)) {
        const workflow = await studentUSIService.get(studentBookingId);
        rows = workflow?.history || workflow?.History || [];
      } else {
        const allRows = courseHistoryCache || await getCourseProgressHistory(studentBookingId);
        if (!courseHistoryCache) setCourseHistoryCache(allRows);
        const stepTypeNo = getCourseStepTypeNo(item);
        rows = Array.isArray(allRows)
          ? allRows.filter((row) => Number(row.stepTypeNo ?? row.StepTypeNo ?? 0) === stepTypeNo)
          : [];
      }

      setStepHistoryCache((current) => ({ ...current, [historyKey]: { rows: Array.isArray(rows) ? rows : [], loading: false, error: "" } }));
    } catch (requestError) {
      setStepHistoryCache((current) => ({
        ...current,
        [historyKey]: { rows: [], loading: false, error: requestError.response?.data?.message || "Unable to load workflow history." },
      }));
    }
  };

  const toggleStepHistory = async (item) => {
    const historyKey = getStepHistoryKey(item);
    if (!historyKey) return;
    const shouldOpen = !stepHistoryOpen[historyKey];
    setStepHistoryOpen((current) => ({ ...current, [historyKey]: shouldOpen }));
    if (shouldOpen) await loadStepHistory(item);
  };

  if (loading) {
    return (
      <div className="wc-card flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading course progress...
      </div>
    );
  }

  if (error || !progress) {
    return (
      <div className="wc-card p-8 text-center">
        <h1 className="text-xl font-display font-extrabold text-[#0A2540]">Course not found</h1>
        <p className="mt-2 text-sm text-slate-500">{error || "This course could not be loaded."}</p>
        <Link to={isAdmin ? "/admin/enrolments" : "/student/my-courses"} className="wc-btn-primary mt-5 inline-flex">{isAdmin ? "Back to Enrolments" : "Back to My Courses"}</Link>
      </div>
    );
  }

  const statusMeta = getStatusMeta(progress.workFlowStatusNo);
  const sections = normalizeSections(progress);
  const groupedSections = buildGroupedSections(sections);
  const visibleGroupedSections = isAdmin
    ? groupedSections
    : groupedSections.filter((group) => group.key !== "in-class-completion");
  const enrolmentSubmittedForStudent = !isAdmin && String(statusMeta.label || "").toLowerCase() === "submitted";
  const nextAction = getVisibleNextAction(progress.nextAction, sections, isAdmin, enrolmentSubmittedForStudent);
  const nextActionStep = sections
    .flatMap((section) => section.items || [])
    .find((step) => String(step.key) === String(nextAction?.key) || step.title === nextAction?.title);
  const nextActionIsAttendance = isAttendanceItem(nextActionStep || nextAction)
    || String(nextAction?.title || "").toLowerCase().includes("attendance");
  const completedRequiredSteps = Number(progress.studentCompletedStepCount ?? progress.completedRequiredSteps ?? progress.completedStepCount ?? 0);
  const totalRequiredSteps = Number(progress.studentTotalStepCount ?? progress.totalRequiredSteps ?? progress.totalStepCount ?? 0);
  const studentRequirementsComplete = !isAdmin && totalRequiredSteps > 0 && completedRequiredSteps >= totalRequiredSteps;
  const nextActionContext = getNextActionContext(nextAction, sections);
  const nextActionWaitingForReview = toDisplayStatus(nextAction?.displayStatus) === DISPLAY_STATUS.PendingReview;
  const focusNextRequiredAction = () => {
    if (!nextAction) return;
    if (nextAction.url && !nextActionWaitingForReview && !nextActionIsAttendance) {
      goToAction(nextAction.url, nextAction);
      return;
    }
    const target = document.getElementById(getCourseStepElementId(nextActionStep?.key || nextAction.key));
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    target?.focus({ preventScroll: true });
  };
  const focusValidationStep = (entry) => {
    const stepTypeNo = Number(entry?.stepTypeNo ?? entry?.StepTypeNo ?? 0);
    const item = sections.flatMap((section) => section.items || []).find((step) => getCourseStepTypeNo(step) === stepTypeNo);
    if (!item) return;

    setStepValidationDialog(null);
    window.requestAnimationFrame(() => {
      const target = document.getElementById(getCourseStepElementId(item.key));
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    });
  };
  // Course progress is a learner record when opened from the staff portal.
  // Staff can inspect every page and history, but do not change workflow here.
  const adminActionButtons = [];
  const canDownloadEnrolmentForm = canDownloadEnrollmentDocument(progress.workFlowStatusNo);
  const studentSummary = getStudentProgressMessage(progress);
  const instituteSummary = getInstituteProgressMessage(progress, isAdmin);

  return (
    <div className={`space-y-6 ${adminActionButtons.length > 0 ? "pb-28" : ""}`} data-testid="course-progress">
      <button type="button" onClick={() => navigate(isAdmin ? "/admin/enrolments" : "/student/my-courses")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-orange-600">
        <ChevronLeft className="h-4 w-4" /> {isAdmin ? "Enrolments" : "My Courses"}
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-orange-600 font-semibold">{progress.courseCode || "Course"}</div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">{progress.courseName}</h1>
          {isAdmin && location.state?.enrolment?.studentName && <p className="mt-1 text-sm font-semibold text-slate-600">{location.state.enrolment.studentName}</p>}
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? `${completedRequiredSteps} of ${totalRequiredSteps} student actions complete (${progress.studentProgressPercentage}%).`
              : progress.studentProgressPercentage >= 100 && !progress.studentHasActionRequired
                ? "All required student actions complete."
                : `${completedRequiredSteps} of ${totalRequiredSteps} student actions complete (${progress.studentProgressPercentage}%).`}
          </p>
          <div className="mt-2 h-2 max-w-md overflow-hidden rounded-full bg-slate-200" aria-label={`Student progress ${progress.studentProgressPercentage}%`}>
            <div className={`h-full transition-all ${progress.studentProgressPercentage >= 100 && !progress.studentHasActionRequired ? "bg-emerald-500" : "bg-orange-500"}`} style={{ width: `${Math.max(0, Math.min(100, Number(progress.studentProgressPercentage) || 0))}%` }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {progress.campus || "Campus TBC"}</span>
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {formatDate(progress.classDate)}</span>
              {progress.deliveryMethod && <span>{progress.deliveryMethod}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <span className="wc-badge bg-slate-100 text-slate-700 border border-slate-200">Booking {progress.bookingNumber || "-"}</span>
          <span className="wc-badge bg-slate-100 text-slate-700 border border-slate-200">Enrolment {progress.enrolmentNumber || "Not created"}</span>
          <span className={`wc-badge border ${statusMeta.className}`}>{statusMeta.label}</span>
          {isAdmin && (
            <>
              <div className="basis-full text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Overall course progress</div>
              <div className="basis-full text-right text-3xl font-display font-extrabold text-[#0A2540]">{progress.progressPercentage}%</div>
            </>
          )}
          </div>
      </div>

      <div className={`grid gap-4 ${isAdmin ? "lg:grid-cols-3" : "grid-cols-1"}`}>
        <CourseProgressSummary
          title={isAdmin ? "Student Progress" : "Your Progress"}
          percent={progress.studentProgressPercentage}
          completed={progress.studentCompletedStepCount}
          total={progress.studentTotalStepCount}
          tone={studentSummary.tone}
          message={studentSummary.message}
          detail={studentSummary.detail}
        />
        {isAdmin && (
          <CourseProgressSummary
            title="Institute Progress"
            percent={progress.instituteProgressPercentage}
            completed={progress.instituteCompletedStepCount}
            total={progress.instituteTotalStepCount}
            tone={instituteSummary.tone}
            message={instituteSummary.message}
            detail={instituteSummary.detail}
          />
        )}
        {isAdmin && (
          <CourseProgressSummary
            title="Overall Course Progress"
            percent={progress.progressPercentage}
            completed={progress.completedStepCount}
            total={progress.totalStepCount}
            tone={progress.progressPercentage >= 100 ? "green" : "blue"}
            message={progress.progressPercentage >= 100 ? "All course workflow steps are complete." : "Student and institute workflow progress combined."}
            detail="This combines student-managed and institute-managed steps for reference."
          />
        )}
      </div>

      {nextAction && !studentRequirementsComplete && (
        <div
          className="wc-card cursor-pointer border-orange-200 bg-gradient-to-r from-orange-50 via-white to-blue-50 p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-300"
          role="button"
          tabIndex={0}
          aria-label={`Go to current step: ${nextAction.title}`}
          onClick={focusNextRequiredAction}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") focusNextRequiredAction();
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-orange-700">Next Required Action</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-extrabold text-[#0A2540]">{nextAction.title}</h2>
                {nextAction.displayStatus && <span className={`wc-badge border ${getStatusStyleClass(nextAction, nextAction.displayStatus)}`}>{nextAction.displayStatus}</span>}
              </div>
              <p className="mt-1 text-sm text-slate-600">{nextAction.description}</p>
              {nextActionContext && <p className="mt-2 text-xs font-semibold text-slate-500">{nextActionContext}</p>}
              <p className="mt-1 text-xs font-semibold text-orange-700">{nextActionWaitingForReview ? "Waiting for institute review" : "Please complete before class"}</p>
            </div>
            {(isAdmin || nextAction.url) && !nextActionWaitingForReview && (
              <button type="button" onClick={(event) => { event.stopPropagation(); focusNextRequiredAction(); }} className="wc-btn-primary justify-center text-sm">
                {isAdmin ? "Go to that step" : nextAction.buttonText || "Continue"}
              </button>
            )}
          </div>
        </div>
      )}

      {studentRequirementsComplete && (
        <div className="wc-card border-emerald-200 bg-emerald-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Pre-Class Requirements Complete</div>
          <p className="mt-2 text-sm text-slate-700">You have completed all required student actions. The Institute will manage the remaining class and completion steps.</p>
        </div>
      )}

      <div className="space-y-5">
        {visibleGroupedSections.map((group) => (
          <CourseProgressGroup key={group.key} group={group} currentActionKey={nextAction?.key} onAction={goToAction} onCompleteStep={handleAdminCompleteStep} onIssueCertificate={openCertificateDialog} onDownloadCertificate={downloadIssuedCertificate} onDownloadEnrolment={downloadEnrollmentForm} canDownloadEnrolmentForm={canDownloadEnrolmentForm} actionDisabled={processingAction} assessmentSummary={assessmentSummary} isAdmin={isAdmin} enrolmentSubmittedForStudent={enrolmentSubmittedForStudent} stepHistoryOpen={stepHistoryOpen} stepHistoryCache={stepHistoryCache} onToggleHistory={toggleStepHistory} attendanceRecord={attendanceRecord} attendanceSaving={attendanceSaving} onAttendanceStatusChange={saveAttendanceStatus} />
        ))}
      </div>
      {stepValidationDialog && (
        <CourseStepValidationDialog
          validation={stepValidationDialog}
          onClose={() => setStepValidationDialog(null)}
          onStepClick={focusValidationStep}
          onSkipLln={handleSkipLlnAndProceed}
          canSkipLlnStep={progress?.settingPerm?.canSkipLlnStep === true}
          courseSteps={sections.flatMap((section) => section.items || [])}
          isAdmin={isAdmin}
        />
      )}
      {lockedStepDialog && (
        <LockedStepWarningDialog
          item={lockedStepDialog}
          onClose={() => setLockedStepDialog(null)}
        />
      )}
      {bookClassDialog && (
        <BookClassConfirmDialog
          progress={progress}
          item={bookClassDialog}
          remarks={bookClassRemarks}
          processing={processingAction}
          onChange={setBookClassRemarks}
          onCancel={() => {
            if (processingAction) return;
            setBookClassDialog(null);
            setBookClassRemarks("");
          }}
          onConfirm={() => completeAdminStep(bookClassDialog, bookClassRemarks)}
        />
      )}
      {courseCompleteDialog && (
        <CourseCompleteConfirmDialog
          item={courseCompleteDialog}
          remarks={courseCompleteRemarks}
          processing={processingAction}
          onChange={setCourseCompleteRemarks}
          onCancel={() => {
            if (processingAction) return;
            setCourseCompleteDialog(null);
            setCourseCompleteRemarks("");
          }}
          onConfirm={() => completeAdminStep(courseCompleteDialog, courseCompleteRemarks)}
        />
      )}
      {certificateDialog && (
        <CertificateDialog
          value={{ mode: "create", form: { ...emptyCertificateForm, enrollmentId: String(progress.studentEnrollmentId || "") } }}
          eligible={[{
            enrollmentId: progress.studentEnrollmentId,
            studentName: location.state?.enrolment?.studentName || "Student",
            studentEmail: location.state?.enrolment?.studentEmail || "",
            courseName: progress.courseName,
            bookingNumber: progress.bookingNumber,
            certificateExists: false,
          }]}
          onClose={() => setCertificateDialog(false)}
          onSaved={async () => {
            setCertificateDialog(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function CourseCompleteConfirmDialog({ item, remarks, processing, onChange, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4 py-6">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Complete Course</h2>
            <p className="mt-1 text-sm text-slate-500">Confirm that all course requirements have been completed.</p>
          </div>
          <button type="button" onClick={onCancel} disabled={processing} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50" aria-label="Close course completion dialog">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
            Completing {item?.title || "this course"} will unlock certificate issuance.
          </div>
          <label className="block">
            <span className="text-sm font-extrabold text-[#0A2540]">Admin remark <span className="font-normal text-slate-500">(optional)</span></span>
            <textarea value={remarks} onChange={(event) => onChange(event.target.value)} className="mt-2 min-h-[104px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" placeholder="Add a note to the course progress history." />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onCancel} disabled={processing} className="wc-btn-outline">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={processing} className="wc-btn-primary">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
            {processing ? "Completing..." : "Complete Course"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LockedStepWarningDialog({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="locked-step-warning-title">
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="h-5 w-5" /></span>
            <h2 id="locked-step-warning-title" className="text-lg font-display font-extrabold text-[#0A2540]">Student preparation in progress</h2>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" aria-label="Close warning"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-sm font-semibold text-slate-700">{item?.title || "This step"} is locked for admin management.</p>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">The student is still completing the required pre-class preparation. Admin editing and completion actions will be available only after the student submits all required preparation steps.</div>
        </div>
        <div className="flex justify-end border-t border-slate-200 px-5 py-4"><button type="button" onClick={onClose} className="wc-btn-primary">Close</button></div>
      </div>
    </div>
  );
}

function BookClassConfirmDialog({ progress, item, remarks, processing, onChange, onCancel, onConfirm }) {
  const bookingRows = [
    ["Course", progress?.courseName || "-"],
    ["Booking", progress?.bookingNumber || "-"],
    ["Class date", formatDate(progress?.classDate)],
    ["Campus", progress?.campus || "Campus TBC"],
    ["Delivery", progress?.deliveryMethod || "-"],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4 py-6">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Confirm Ready for Class</h2>
            <p className="mt-1 text-sm text-slate-500">Complete the {item?.title || "Ready for Class"} step and unlock the next requirement.</p>
          </div>
          <button type="button" onClick={onCancel} disabled={processing} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" aria-label="Close book class dialog">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {bookingRows.map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-xs font-extrabold uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-sm font-semibold text-[#0A2540]">{value}</div>
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-sm font-extrabold text-[#0A2540]">Admin remark</span>
            <textarea
              value={remarks}
              onChange={(event) => onChange(event.target.value)}
              className="mt-2 min-h-[104px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              placeholder="Add a note for the course progress history."
            />
          </label>
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onCancel} disabled={processing} className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-extrabold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={processing} className="inline-flex h-10 items-center justify-center rounded-lg border border-orange-600 bg-orange-600 px-4 text-sm font-extrabold text-white hover:border-orange-700 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60">
            {processing ? "Completing..." : "Confirm Ready for Class"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminActionFooter({ buttons, disabled, onAction, progressPercentage, statusLabel }) {
  return (
    <div className="fixed bottom-0 right-0 left-64 z-40 border-t border-slate-200 bg-white/95 py-3 shadow-[0_-10px_24px_-18px_rgba(15,23,42,0.55)] backdrop-blur max-lg:left-0">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4">
            <span className="text-xs font-extrabold uppercase tracking-wide text-[#183050]">Admin Actions</span>
            <span className="h-5 w-px bg-slate-200" />
            <span className="whitespace-nowrap text-sm font-semibold text-slate-600">{statusLabel}</span>
            <span className="whitespace-nowrap text-sm font-semibold text-slate-600">Progress: <span className="font-extrabold text-[#183050]">{progressPercentage}%</span></span>
          </div>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2 overflow-x-auto pb-0.5">
          {buttons.map((button) => {
            const label = button.text ?? button.Text;
            return (
              <button
                key={`${button.currentWorkFlowStatusNo ?? button.CurrentWorkFlowStatusNo}-${button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo}`}
                type="button"
                disabled={disabled}
                onClick={(event) => onAction(event, button)}
                data-action={label}
                data-next-workflow-status-no={getWorkflowButtonNextStatus(button)}
                data-workflow-status-no={getWorkflowButtonNextStatus(button)}
                className={`${getAdminActionButtonClass(label)} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {disabled ? "Working..." : label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getAdminActionButtonClass(label = "") {
  const normalized = String(label).toLowerCase();
  const baseClass = "inline-flex h-11 min-w-[104px] shrink-0 items-center justify-center whitespace-nowrap rounded-lg px-5 text-sm font-extrabold shadow-sm transition-colors";
  if (normalized.includes("approve") || normalized.includes("submit")) {
    return `${baseClass} border border-orange-600 bg-orange-600 text-white hover:bg-orange-700 hover:border-orange-700`;
  }
  if (normalized.includes("reject") || normalized.includes("cancel") || normalized.includes("expire")) {
    return `${baseClass} border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:border-rose-300`;
  }
  return `${baseClass} border border-[#183050] bg-white text-[#183050] hover:bg-slate-50`;
}

function CourseProgressGroup({ group, currentActionKey = null, onAction, onCompleteStep, onIssueCertificate, onDownloadCertificate, onDownloadEnrolment, canDownloadEnrolmentForm = false, actionDisabled = false, assessmentSummary, isAdmin = false, enrolmentSubmittedForStudent = false, stepHistoryOpen = {}, stepHistoryCache = {}, onToggleHistory, attendanceRecord = null, attendanceSaving = false, onAttendanceStatusChange }) {
  const enhancedGroupHeader = group.key === "pre-class-requirements" || group.key === "pre-class-learning" || group.key === "in-class-completion";
  const hierarchicalLearning = group.key === "pre-class-learning";
  const hierarchyStepNumbers = new Map();
  let hierarchyStepCount = 0;
  if (hierarchicalLearning) {
    group.segments.forEach((segment) => (segment.items || []).forEach((item) => {
      hierarchyStepCount += 1;
      hierarchyStepNumbers.set(String(item.key), hierarchyStepCount);
    }));
  }
  const [groupCollapsed, setGroupCollapsed] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  const toggleCategory = (category) => setCollapsedCategories((current) => {
    const next = new Set(current);
    if (next.has(category)) next.delete(category); else next.add(category);
    return next;
  });
  return (
    <section className="wc-card p-6">
      {enhancedGroupHeader ? <ProgressGroupHeader group={group} collapsed={groupCollapsed} isAdmin={isAdmin} onToggle={() => setGroupCollapsed((value) => !value)} /> : <>
        <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-200">
          <div className="w-1 h-6 bg-orange-500 rounded-full" />
          <h2 className="text-lg font-display font-extrabold text-[#0A2540]">{group.title}</h2>
        </div>
        {group.description && <p className="mb-4 text-sm text-slate-600">{group.description}</p>}
      </>}
      {!groupCollapsed && <div className="space-y-4">
        {hierarchicalLearning && (
          <div className="mb-5 flex flex-col gap-3 rounded-xl border border-blue-200 bg-[#f5f9fd] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#3f7fd6]">Your preparation pathway</div>
              <p className="mt-1 text-sm text-slate-600">Work from top to bottom. Finish each numbered step to unlock the next one.</p>
            </div>
            <span className="inline-flex w-fit rounded-full bg-[#183050] px-3 py-1.5 text-xs font-extrabold text-white">{hierarchyStepCount} steps in order</span>
          </div>
        )}
        {group.segments.map((segment, index) => {
          const category = getSegmentCategory(segment, group.key);
          const categoryCollapsed = collapsedCategories.has(category);
          const categoryHeader = isLearningCompetencySegment(segment) || isReadingResourcesSegment(segment);
          if (categoryCollapsed && !categoryHeader) return null;
          const categoryClass = category === "learning"
            ? "rounded-xl border border-blue-100 bg-blue-50/50 p-3"
            : category === "reading"
              ? "rounded-xl border border-amber-100 bg-amber-50/50 p-3"
              : category === "requirements"
                ? "rounded-xl border border-emerald-100 bg-emerald-50/40 p-3"
                : category === "in-class"
                  ? "rounded-xl border border-violet-100 bg-violet-50/40 p-3"
              : index > 0 ? "border-t border-slate-200 pt-5" : "";
          return (
          <div key={segment.key} className={`${categoryClass} ${hierarchicalLearning ? "relative ml-3 border-l-2 border-l-blue-300 pl-6 sm:ml-6 sm:pl-8" : ""}`}>
            {hierarchicalLearning && (
              <span className="absolute -left-[11px] top-5 h-5 w-5 rounded-full border-4 border-white bg-[#3f7fd6] shadow-sm" aria-hidden="true" />
            )}
            {segment.title && (
              (isLearningCompetencySegment(segment) || isReadingResourcesSegment(segment)) && segment.subheading ? (
                <ProgressSectionHeader segment={segment} collapsed={categoryCollapsed} onToggle={() => toggleCategory(category)} />
              ) : (
                <>
                  <h3 className="text-base font-display font-extrabold text-[#0A2540]">{segment.title}</h3>
                  {segment.subheading && <p className="mt-2 mb-4 text-sm text-slate-600">{segment.subheading}</p>}
                </>
              )
            )}
            {!categoryCollapsed && <div className="space-y-3">
              {(segment.items || []).map((item) => (
                <div key={item.key} className={hierarchicalLearning ? "relative pl-9 sm:pl-11" : ""}>
                  {hierarchicalLearning && (
                    <div className="absolute left-0 top-5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-blue-200 bg-white text-xs font-extrabold text-[#183050] shadow-sm sm:h-8 sm:w-8">
                      {hierarchyStepNumbers.get(String(item.key))}
                    </div>
                  )}
                  <CourseProgressItem item={item} category={category} isCurrentStep={String(item.key) === String(currentActionKey)} onAction={onAction} onCompleteStep={onCompleteStep} onIssueCertificate={onIssueCertificate} onDownloadCertificate={onDownloadCertificate} onDownloadEnrolment={onDownloadEnrolment} canDownloadEnrolmentForm={canDownloadEnrolmentForm} actionDisabled={actionDisabled} assessmentSummary={assessmentSummary} showAssessmentSummary={isPreClassItem(item)} isAdmin={isAdmin} enrolmentSubmittedForStudent={enrolmentSubmittedForStudent} stepHistoryOpen={stepHistoryOpen} stepHistoryCache={stepHistoryCache} onToggleHistory={onToggleHistory} attendanceRecord={attendanceRecord} attendanceSaving={attendanceSaving} onAttendanceStatusChange={onAttendanceStatusChange} />
                </div>
              ))}
            </div>}
          </div>
        );})}
      </div>}
    </section>
  );
}

function ProgressGroupHeader({ group, collapsed, isAdmin = false, onToggle }) {
  const requirements = group.key === "pre-class-requirements";
  const learning = group.key === "pre-class-learning";
  const Icon = requirements ? IdCard : learning ? BookOpen : GraduationCap;
  const panelClass = requirements
    ? "border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-cyan-50"
    : learning
      ? "border-orange-200 bg-gradient-to-r from-orange-50 via-white to-blue-50"
      : "border-violet-200 bg-gradient-to-r from-violet-50 via-white to-blue-50";
  const iconClass = requirements ? "bg-emerald-700" : learning ? "bg-[#183050]" : "bg-violet-700";
  const labelClass = requirements ? "text-emerald-700" : learning ? "text-orange-600" : "text-violet-700";
  const badgeClass = requirements
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : learning
      ? "border-orange-200 bg-orange-50 text-orange-700"
      : "border-violet-200 bg-violet-50 text-violet-700";
  const accentClass = requirements
    ? "from-emerald-600 via-cyan-500 to-[#3f7fd6]"
    : learning
      ? "from-orange-500 via-orange-400 to-[#3f7fd6]"
      : "from-violet-600 via-[#3f7fd6] to-orange-400";
  const managedStepLabels = group.key !== "in-class-completion" || !isAdmin
    ? []
    : group.segments
      .flatMap((segment) => segment.items || [])
      .filter((item) => {
        const permissions = getCourseProgressPermissions({ role: "admin", step: item });
        return permissions.canManageStep || permissions.hasCertificateIssuePermission;
      })
      .map((item) => item.title)
      .filter((title, index, labels) => labels.indexOf(title) === index);
  return (
    <div className={`mb-5 overflow-hidden rounded-xl border shadow-sm ${panelClass}`}>
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-sm ${iconClass}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className={`mb-1 text-[10px] font-extrabold uppercase tracking-[0.18em] ${labelClass}`}>{requirements ? "Before training" : learning ? "Preparation pathway" : "Training and completion"}</div>
          <h2 className="text-lg font-display font-extrabold text-[#0A2540]">{group.title}</h2>
          {group.description && <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">{group.description}</p>}
          {managedStepLabels.length > 0 && (
            <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" title={`You can manage: ${managedStepLabels.join(", ")}`}>
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate">You can manage: {managedStepLabels.join(", ")}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1.5 text-xs font-extrabold ${badgeClass}`}>{requirements || learning ? "Complete before class" : "Completed during class"}</span>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label={group.title} />
        </div>
      </div>
      <div className={`h-1 bg-gradient-to-r ${accentClass}`} />
    </div>
  );
}

function ProgressSectionHeader({ segment, collapsed, onToggle }) {
  const reading = isReadingResourcesSegment(segment);
  const Icon = reading ? BookOpen : ClipboardList;
  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-[#c9dcef] bg-gradient-to-r from-[#f3f8fd] via-white to-[#fff7ed] shadow-sm">
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#183050] text-white shadow-sm">
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-orange-600">Pre-class preparation</div>
          <h3 className="text-lg font-display font-extrabold text-[#0A2540]">{segment.title}</h3>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">{segment.subheading}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="inline-flex w-fit items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-extrabold text-orange-700">Please complete before class</span>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label={segment.title} />
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-[#3f7fd6]" />
    </div>
  );
}

function CollapseButton({ collapsed, onToggle, label }) {
  return <button type="button" onClick={onToggle} aria-expanded={!collapsed} aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-[#183050]">
    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${collapsed ? "-rotate-90" : "rotate-0"}`} />
  </button>;
}

function CourseProgressItem({ item, category = "", isCurrentStep = false, onAction, onCompleteStep, onIssueCertificate, onDownloadCertificate, onDownloadEnrolment, canDownloadEnrolmentForm = false, actionDisabled = false, assessmentSummary, showAssessmentSummary = false, isAdmin = false, enrolmentSubmittedForStudent = false, stepHistoryOpen = {}, stepHistoryCache = {}, onToggleHistory, attendanceRecord = null, attendanceSaving = false, onAttendanceStatusChange }) {
  const Icon = iconMap[item.icon] || FileText;
  const StatusIcon = BadgeCheck;
  const statusNo = Number(item.statusNo ?? item.stepStatusNo ?? 0);
  const status = item.status || ({ 1: "Locked", 2: "Available", 3: "InProgress", 4: "Completed", 5: "ActionRequired" }[statusNo] || "Locked");
  const displayStatus = getVisibleDisplayStatus(item, status, isAdmin, enrolmentSubmittedForStudent);
  const statusMessage = item.studentStatusMessage || item.statusMessage || "";
  const reviewStatus = item.reviewStatusText || item.reviewStatus || item.workflowStatusName || "";
  const serverStatusStyle = getServerStatusStyle(item);
  const completed = serverStatusStyle
    ? serverStatusStyle === STATUS_STYLE.Completed
    : displayStatus === DISPLAY_STATUS.Completed || Boolean(item.isCompleted);
  const locked = Boolean(item.isLocked)
    || (serverStatusStyle
      ? serverStatusStyle === STATUS_STYLE.Locked
      : displayStatus === DISPLAY_STATUS.Locked);
  const isResource = item.sectionKey === "resources";
  const isAssessment = item.sectionKey === "assessments";
  const effectiveUrl = getEffectiveStepUrl(item, isAdmin);
  const adminReviewReady = isPreClassItem(item) && displayStatus === DISPLAY_STATUS.PendingReview;
  const permissions = getCourseProgressPermissions({
    role: isAdmin ? "admin" : "student",
    step: { ...item, url: effectiveUrl, adminReviewReady },
  });
  const canOpen = permissions.canView && (isAdmin || (!locked && item.canOpen !== false));
  const preClassReviewReady = !isPreClassItem(item) || displayStatus === DISPLAY_STATUS.PendingReview;
  // Directly completing a workflow step is a staff action. Student-owned
  // steps must open their form/activity so a draft can be continued.
  const canCompleteStep = isAdmin && permissions.canComplete;
  const isAttendanceStep = isAttendanceItem(item);
  const selectedAttendanceStatusId = Number(attendanceRecord?.attendanceStatusId || 0);
  const canSaveAttendance = isAttendanceStep && permissions.canManageStep && !locked && !completed;
  const canCompleteAttendance = canCompleteStep && (!isAttendanceStep || selectedAttendanceStatusId === ATTENDANCE_STATUS.Present);
  const canIssueCertificate = isCertificateItem(item) && !completed && permissions.canIssueCertificate;
  const hasStepManagementPermission = isAdmin
    && (permissions.canManageStep || (isCertificateItem(item) && permissions.hasCertificateIssuePermission));
  const showEnrolmentDownload = isEnrolmentItem(item) && canDownloadEnrolmentForm && typeof onDownloadEnrolment === "function";
  const historyKey = getStepHistoryKey(item);
  const historyState = stepHistoryCache[historyKey] || { rows: [], loading: false, error: "" };
  const historyVisible = Boolean(stepHistoryOpen[historyKey]);
  const canShowHistory = Boolean(historyKey) && (!locked || historyState.rows.length > 0 || historyState.loading || historyState.error);
  const historyButtonText = historyVisible ? "Hide History" : "History";
  const timingMessage = getStepTimingMessage(item);

  if (isResource || isAssessment) {
    return (
      <div id={getCourseStepElementId(item.key)} tabIndex={-1} className={`relative rounded-lg border px-4 py-3 transition-all hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${isCurrentStep ? "border-blue-300 bg-blue-50 ring-2 ring-blue-300 ring-offset-2 shadow-md" : "border-slate-200 bg-white"}`}>
        {isCurrentStep && <span className="absolute -top-3 right-4 inline-flex items-center rounded-full border border-blue-200 bg-blue-600 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm">Current step</span>}
        <div className="flex min-h-[76px] flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-[#0A2540]">{item.title}</h3>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {canShowHistory && (
              <button type="button" disabled={historyState.loading} onClick={() => onToggleHistory?.(item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                {historyState.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                <span className="hidden sm:inline">{historyState.loading ? "Loading..." : historyButtonText}</span>
              </button>
            )}
            <button type="button" disabled={!canOpen} onClick={() => onAction(effectiveUrl, item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">View</span>
            </button>
            <button type="button" disabled={!canOpen} onClick={() => onAction(effectiveUrl, item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download</span>
            </button>
          </div>
        </div>
        {historyVisible && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <h4 className="mb-4 text-base font-display font-extrabold text-[#0A2540]">Workflow history</h4>
            <WorkflowHistoryTimeline rows={historyState.rows} loading={historyState.loading} error={historyState.error} isAdmin={isAdmin} />
          </div>
        )}
      </div>
    );
  }

  const categoryCardClass = category === "learning"
    ? "border-blue-200 border-l-4 border-l-[#3f7fd6]"
    : category === "reading"
      ? "border-amber-200 border-l-4 border-l-orange-500"
      : category === "requirements"
        ? "border-emerald-200 border-l-4 border-l-emerald-600"
        : category === "in-class"
          ? "border-violet-200 border-l-4 border-l-violet-600"
      : "border-slate-200";

  return (
    <div id={getCourseStepElementId(item.key)} tabIndex={-1} className={`relative rounded-lg border bg-white px-4 py-3 shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${categoryCardClass} ${isCurrentStep ? "!border-blue-300 !bg-blue-50 ring-2 ring-blue-300 ring-offset-2 shadow-md" : ""}`}>
      {isCurrentStep && <span className="absolute -top-3 right-4 inline-flex items-center rounded-full border border-blue-200 bg-blue-600 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm">Current step</span>}
      {hasStepManagementPermission && (
        <span className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm" title="You have permission to manage this step">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">You have permission to manage this step</span>
        </span>
      )}
      <div className="flex min-h-[76px] flex-col gap-3 sm:flex-row sm:items-center">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${getStatusIconClass(item, displayStatus)}`}>
          {locked ? <Lock className="h-5 w-5" /> : completed ? <StatusIcon className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-[#0A2540]">{item.title}</h3>
            <span className={`wc-badge border ${getStatusStyleClass(item, displayStatus)}`}>
              {completed && <BadgeCheck className="mr-1 h-3.5 w-3.5" />}
              {displayStatus}
            </span>
            {isAdmin && reviewStatus && !isSameStatusLabel(displayStatus, reviewStatus) && (
              <span className="wc-badge border border-slate-200 bg-slate-50 text-slate-700">
                Review: {reviewStatus}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">{item.description}</p>
          {isReadingResourcesItem(item) && <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {item.resourceCategory && <span>{item.resourceCategory}</span>}
            {item.resourceFormat && <span>{item.resourceFormat}</span>}
            {Number(item.estimatedMinutes) > 0 && <span>About {item.estimatedMinutes} minutes</span>}
          </div>}
          {timingMessage && <p className="mt-1 text-xs font-extrabold text-orange-600">{timingMessage}</p>}
          {statusMessage && <p className="mt-1 text-sm font-semibold text-slate-600">{statusMessage}</p>}
          {!isAdmin && reviewStatus && !isSameStatusLabel(displayStatus, reviewStatus) && <p className="mt-1 text-xs font-semibold text-slate-400">{reviewStatus}</p>}
          {showAssessmentSummary && <AssessmentAttemptSummary summary={assessmentSummary} isAdmin={isAdmin} displayStatus={displayStatus} />}
          {item.completionDate && <p className="mt-1 text-xs text-slate-400">Completed {formatDate(item.completionDate)}</p>}
        </div>
        {canCompleteAttendance || canSaveAttendance ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {canShowHistory && (
              <button type="button" disabled={historyState.loading} onClick={() => onToggleHistory?.(item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                {historyState.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                {historyState.loading ? "Loading..." : historyButtonText}
              </button>
            )}
            {isPreClassItem(item) && canOpen && (
              <button type="button" disabled={actionDisabled} onClick={() => onAction(effectiveUrl, item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                View
              </button>
            )}
            {canSaveAttendance && (
              <div className="min-w-[220px]">
                <select className="wc-input h-10" disabled={completed || attendanceSaving} value={selectedAttendanceStatusId || ""} onChange={(event) => onAttendanceStatusChange(Number(event.target.value))}>
                  <option value="">Attendance status</option>
                  {ATTENDANCE_STATUS_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </div>
            )}
            {canCompleteAttendance && (
              <button type="button" disabled={actionDisabled || attendanceSaving} onClick={() => onCompleteStep(item)} className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-orange-600 bg-orange-600 px-4 text-sm font-extrabold text-white shadow-sm transition-colors hover:border-orange-700 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60">
                {actionDisabled || attendanceSaving ? "Working..." : isCourseCompletedItem(item) ? "Complete Course" : "Complete"}
              </button>
            )}
          </div>
        ) : isCertificateItem(item) && completed ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {canShowHistory && (
              <button type="button" disabled={historyState.loading} onClick={() => onToggleHistory?.(item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                {historyState.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                {historyState.loading ? "Loading..." : historyButtonText}
              </button>
            )}
            <button type="button" disabled={actionDisabled} onClick={onDownloadCertificate} className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#183050] bg-white px-4 text-sm font-extrabold text-[#183050] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">
              <Download className="h-4 w-4" /> Download Certificate
            </button>
          </div>
        ) : canIssueCertificate ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {canShowHistory && (
              <button type="button" disabled={historyState.loading} onClick={() => onToggleHistory?.(item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                {historyState.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                {historyState.loading ? "Loading..." : historyButtonText}
              </button>
            )}
            <button type="button" disabled={actionDisabled} onClick={() => onIssueCertificate(item)} className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-orange-600 bg-orange-600 px-4 text-sm font-extrabold text-white shadow-sm transition-colors hover:border-orange-700 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60">
              <BadgeCheck className="h-4 w-4" /> Issue Certificate
            </button>
          </div>
        ) : !isAdmin && isBookClassItem(item) ? null : (
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {canShowHistory && (
              <button type="button" disabled={historyState.loading} onClick={() => onToggleHistory?.(item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                {historyState.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                {historyState.loading ? "Loading..." : historyButtonText}
              </button>
            )}
            {showEnrolmentDownload && (
              <button type="button" onClick={onDownloadEnrolment} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-[#183050] bg-white px-3 text-sm font-semibold text-[#183050] transition-colors hover:bg-slate-50">
                <Download className="h-4 w-4" />
                Download Enrolment
              </button>
            )}
            <button type="button" disabled={!canOpen} onClick={() => onAction(effectiveUrl, item)} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              {getVisibleActionText(item, isAdmin, enrolmentSubmittedForStudent)}
            </button>
          </div>
        )}
      </div>
      {historyVisible && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <h4 className="mb-4 text-base font-display font-extrabold text-[#0A2540]">Workflow history</h4>
          <WorkflowHistoryTimeline rows={historyState.rows} loading={historyState.loading} error={historyState.error} isAdmin={isAdmin} />
        </div>
      )}
    </div>
  );
}

function getVisibleNextAction(nextAction, sections, isAdmin, enrolmentSubmittedForStudent) {
  if (!nextAction) {
    return isAdmin ? getFirstPendingStep(sections, true) : null;
  }

  if (isAdmin) {
    const matchingItem = sections
      .flatMap((section) => section.items || [])
      .find((item) => String(item.key) === String(nextAction.key) || item.title === nextAction.title);
    const permissionStep = matchingItem || nextAction;
    return {
      ...nextAction,
      key: permissionStep.key || nextAction.key,
      url: isAttendanceItem(permissionStep) ? "" : nextAction.url || getEffectiveStepUrl(permissionStep, true),
      // Staff course progress is read-only: always take them to the relevant
      // page instead of presenting a workflow action such as “Start”.
      buttonText: "Go to that step",
    };
  }

  return nextAction;
}

function getNextActionContext(nextAction, sections = []) {
  if (!nextAction) return "";
  const item = sections
    .flatMap((section) => section.items || [])
    .find((candidate) => String(candidate?.key) === String(nextAction.key));
  const stepTypeNo = getCourseStepTypeNo(item || nextAction);
  if ([COURSE_STEP_TYPE.PRE_TRAINING_REVIEW, COURSE_STEP_TYPE.LLN_TEST, COURSE_STEP_TYPE.PRE_CLASS_PREPARATION].includes(stepTypeNo)) {
    return "Pre-Class Learning › Learning Competency Assessment";
  }
  if ([COURSE_STEP_TYPE.READING_LEARNING_PACK, COURSE_STEP_TYPE.READING_VICTORIAN_SAFETY, COURSE_STEP_TYPE.READING_WORKSAFE].includes(stepTypeNo)) {
    return "Pre-Class Learning › Reading Resources";
  }
  return "";
}

function getFirstPendingStep(sections = [], isAdmin = false) {
  const nextItem = sections
    .flatMap((section) => section.items || [])
    .find((item) => {
      const displayStatus = toDisplayStatus(item?.displayStatus || item?.status);
      return item
        && displayStatus !== DISPLAY_STATUS.Completed
        && displayStatus !== DISPLAY_STATUS.Locked
        && !Boolean(item.isCompleted)
        && !Boolean(item.isLocked);
    });

  if (!nextItem) return null;

  return {
    key: nextItem.key,
    title: nextItem.title,
    description: nextItem.studentStatusMessage || nextItem.statusMessage || nextItem.description,
    studentWorkflowStatusNo: nextItem.studentWorkflowStatusNo,
    studentWorkflowStatus: nextItem.studentWorkflowStatus,
    displayStatus: getVisibleDisplayStatus(nextItem, nextItem.status),
    statusStyleNo: nextItem.statusStyleNo,
    statusStyle: nextItem.statusStyle,
    statusColour: nextItem.statusColour,
    reviewStatus: nextItem.reviewStatus,
    reviewStatusText: nextItem.reviewStatusText,
    studentStatusMessage: nextItem.studentStatusMessage,
    requiresStudentAction: nextItem.requiresStudentAction,
    isStudentAction: nextItem.isStudentAction,
    actionType: nextItem.actionType,
    buttonText: nextItem.primaryActionText || nextItem.actionText || "Start",
    url: getEffectiveStepUrl(nextItem, isAdmin),
  };
}

function getEffectiveStepUrl(item = {}, isAdmin = false) {
  if (!isAdmin) return item.url || "";
  const key = String(item.key || item.stepCode || item.title || "").toLowerCase();
  if (key.includes("attendance")) return "/admin/attendance";
  return item.url || "";
}

function getVisibleDisplayStatus(item, status, isAdmin, enrolmentSubmittedForStudent) {
  const studentWorkflowStatus = item.studentWorkflowStatus || item.displayStatus || "";
  if (studentWorkflowStatus) return toDisplayStatus(studentWorkflowStatus);
  if (!isAdmin && isEnrolmentItem(item) && enrolmentSubmittedForStudent) return DISPLAY_STATUS.Completed;
  return item.displayStatus || toDisplayStatus(status);
}

function getVisibleActionText(item, isAdmin, enrolmentSubmittedForStudent) {
  const permissions = getCourseProgressPermissions({ role: isAdmin ? "admin" : "student", step: item });
  if (isAdmin && permissions.isStudentOwned) return "View";
  if (isAdmin && permissions.isLln) return "View";
  // A started student task resumes the existing work.  Do this before using
  // the server's generic primary-action label (which can be "Complete").
  if (!isAdmin && getVisibleDisplayStatus(item, item.status, isAdmin, enrolmentSubmittedForStudent) === DISPLAY_STATUS.InProgress) {
    return "Continue";
  }
  const actionType = String(item.actionType || "").toLowerCase();
  if (actionType === "start") return "Start";
  if (actionType === "continue") return "Continue";
  if (actionType === "update") return "Update";
  if (actionType === "resubmit") return "Resubmit";
  if (actionType === "fix") return "Fix";
  if (actionType === "view") return "View";
  if (!isAdmin && isEnrolmentItem(item) && (enrolmentSubmittedForStudent || toDisplayStatus(item.displayStatus) === DISPLAY_STATUS.PendingReview)) {
    return "View";
  }
  return item.primaryActionText || item.actionText || "View";
}

function toDisplayStatus(status = "") {
  const normalized = String(status).replace(/\s+/g, "").toLowerCase();
  if (normalized === "inprogress") return DISPLAY_STATUS.InProgress;
  if (normalized === "actionrequired") return DISPLAY_STATUS.ActionRequired;
  if (normalized === "pendingreview") return DISPLAY_STATUS.PendingReview;
  if (normalized === "submitted") return DISPLAY_STATUS.Submitted;
  if (normalized === "completed" || normalized.startsWith("complete(") || normalized.startsWith("completed(") || normalized === "done") return DISPLAY_STATUS.Completed;
  if (normalized === "locked") return DISPLAY_STATUS.Locked;
  return DISPLAY_STATUS.Available;
}

function getServerStatusStyle(source = {}) {
  const style = String(source?.statusStyle || source?.StatusStyle || "").trim();
  if (Object.values(STATUS_STYLE).includes(style)) return style;

  const styleNo = Number(source?.statusStyleNo ?? source?.StatusStyleNo ?? 0);
  return ({
    1: STATUS_STYLE.Completed,
    2: STATUS_STYLE.Pending,
    3: STATUS_STYLE.Available,
    4: STATUS_STYLE.Locked,
    5: STATUS_STYLE.Error,
  })[styleNo] || "";
}

function getServerStatusColour(source = {}) {
  const colour = String(source?.statusColour || source?.StatusColour || "").trim();
  return [...Object.values(STATUS_COLOUR), "Blue"].includes(colour) ? colour : "";
}

function getStatusStyleClass(source = {}, fallbackStatus = "") {
  const colour = getServerStatusColour(source);
  const style = getServerStatusStyle(source);
  if (colour === STATUS_COLOUR.Green || (!colour && style === STATUS_STYLE.Completed)) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (colour === "Blue") return "bg-blue-50 text-blue-700 border-blue-200";
  if (colour === STATUS_COLOUR.Amber || (!colour && style === STATUS_STYLE.Pending)) return "bg-amber-50 text-amber-700 border-amber-200";
  if (colour === STATUS_COLOUR.Orange || (!colour && style === STATUS_STYLE.Available)) return "bg-orange-50 text-orange-700 border-orange-200";
  if (colour === STATUS_COLOUR.Grey || (!colour && style === STATUS_STYLE.Locked)) return "bg-slate-100 text-slate-500 border-slate-200";
  if (colour === STATUS_COLOUR.Red || (!colour && style === STATUS_STYLE.Error)) return "bg-rose-50 text-rose-700 border-rose-200";
  return getDisplayStatusClass(fallbackStatus);
}

function getStatusIconClass(source = {}, fallbackStatus = "") {
  const colour = getServerStatusColour(source);
  const style = getServerStatusStyle(source);
  if (colour === STATUS_COLOUR.Green || (!colour && style === STATUS_STYLE.Completed)) return "bg-emerald-50 text-emerald-600";
  if (colour === "Blue") return "bg-blue-50 text-blue-600";
  if (colour === STATUS_COLOUR.Amber || (!colour && style === STATUS_STYLE.Pending)) return "bg-amber-50 text-amber-600";
  if (colour === STATUS_COLOUR.Orange || (!colour && style === STATUS_STYLE.Available)) return "bg-orange-50 text-orange-600";
  if (colour === STATUS_COLOUR.Grey || (!colour && style === STATUS_STYLE.Locked)) return "bg-slate-100 text-slate-400";
  if (colour === STATUS_COLOUR.Red || (!colour && style === STATUS_STYLE.Error)) return "bg-rose-50 text-rose-600";

  const normalized = toDisplayStatus(fallbackStatus);
  if (normalized === DISPLAY_STATUS.Completed) return "bg-emerald-50 text-emerald-600";
  if (normalized === DISPLAY_STATUS.Locked) return "bg-slate-100 text-slate-400";
  if (normalized === DISPLAY_STATUS.InProgress) return "bg-blue-50 text-blue-600";
  if (normalized === DISPLAY_STATUS.PendingReview || normalized === DISPLAY_STATUS.Submitted) return "bg-amber-50 text-amber-600";
  if (normalized === DISPLAY_STATUS.ActionRequired) return "bg-rose-50 text-rose-600";
  return "bg-orange-50 text-orange-600";
}

function getDisplayStatusClass(status = "") {
  const normalized = toDisplayStatus(status);
  if (normalized === DISPLAY_STATUS.Completed) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (normalized === DISPLAY_STATUS.Locked) return "bg-slate-100 text-slate-500 border-slate-200";
  if (normalized === DISPLAY_STATUS.ActionRequired) return "bg-rose-50 text-rose-700 border-rose-200";
  if (normalized === DISPLAY_STATUS.InProgress) return "bg-blue-50 text-blue-700 border-blue-200";
  if (normalized === DISPLAY_STATUS.PendingReview || normalized === DISPLAY_STATUS.Submitted) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-orange-50 text-orange-700 border-orange-200";
}

function isSameStatusLabel(left = "", right = "") {
  return String(left).replace(/\s+/g, "").toLowerCase() === String(right).replace(/\s+/g, "").toLowerCase();
}

function AssessmentAttemptSummary({ summary, isAdmin = false, displayStatus = "" }) {
  const normalizedDisplayStatus = toDisplayStatus(displayStatus);
  if (!summary) {
    if ([DISPLAY_STATUS.PendingReview, DISPLAY_STATUS.Completed, DISPLAY_STATUS.Submitted].includes(normalizedDisplayStatus)) return null;
    return <div className="mt-2 text-xs font-semibold text-slate-400">Assessment attempt not started.</div>;
  }
  return <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold">
    {summary.resumeText && <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-orange-700">{summary.resumeText}</span>}
    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">Attempt: {summary.status}</span>
    <span className={`rounded-full border px-2.5 py-1 ${summary.support ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{summary.support ? "Support recommended" : "On track"}</span>
    {isAdmin ? <>
      <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700">Score {summary.correct}/{summary.total}</span>
      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">Correct {summary.correct}</span>
      <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700">Wrong {summary.wrong}</span>
    </> : <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700">Answered {summary.answered}/{summary.total}</span>}
  </div>;
}

function getStepTimingMessage(item = {}) {
  const stepTypeNo = getCourseStepTypeNo(item);
  if (isEnrolmentItem(item) || isDocumentsItem(item) || isUsiItem(item) || isCompetencyItem(item) || isReadingResourcesItem(item)) return "Please complete before class";
  if (isBookClassItem(item) || isAttendanceItem(item) || stepTypeNo === ASSESSMENT_STEP_TYPE) return "Completed during class";
  if (isCourseCompletedItem(item) || isCertificateItem(item)) return "Available after all training and assessment requirements are completed";
  return "";
}
